<template>
  <!-- Table Container Card -->
  <b-card no-body>

    <div class="m-2">

      <!-- Table Top -->
      <b-row class="align-items-center">
        <!-- Left section: Per Page + Date filters + Export -->
        <b-col
          cols="12"
          lg="8"
          class="d-flex flex-wrap align-items-center"
        >
          <!-- Per Page -->
          <div class="mr-2 mb-1">
            <TableEntries
              v-model="perPage"
              :options="perPageOptions"
            />
          </div>

          <!-- Date Filters -->
          <validation-observer ref="accountMovementRules">
            <b-form
              class="d-flex flex-wrap align-items-end"
              @submit.prevent="handleSubmit"
            >
              <div
                class="mr-2"
                style="min-width: 180px"
              >
                <DatePickerField
                  id="financial-date-from"
                  v-model="filter.financialDateFrom"
                  :min-date="new Date()"
                  :label="$t('Financial Date (From)')"
                  rules="required"
                  :error-name="$t('Financial Date From')"
                  :config="datePickerConfig"
                  :placeholder="$t('Date From')"
                  @input="handleDateChange"
                />
              </div>

              <div
                class="mr-2"
                style="min-width: 180px"
              >
                <DatePickerField
                  id="financial-date-to"
                  v-model="filter.financialDateTo"
                  rules="required"
                  :min-date="new Date()"
                  :label="$t('Financial Date (To)')"
                  :error-name="$t('Financial Date To')"
                  :config="datePickerConfig"
                  :placeholder="$t('Date To')"
                  @input="handleDateChange"
                />
              </div>

              <!-- Export -->
              <div class="mb-2">
                <b-dropdown
                  v-if="getUserPermission('EXPORT_ACCOUNT_BALANCE_MOVEMENT_REPORT')"
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  :text="$t('Export')"
                  variant="primary"
                  :disabled="isDisabled"
                >
                  <b-dropdown-item @click="csvExport">
                    {{ $t('CSV') }}
                  </b-dropdown-item>
                  <b-dropdown-item @click="excelExport">
                    {{ $t('Excel') }}
                  </b-dropdown-item>
                  <b-dropdown-item @click="pdfExport">
                    {{ $t('PDF') }}
                  </b-dropdown-item>
                </b-dropdown>
              </div>
            </b-form>
          </validation-observer>
        </b-col>

        <!-- Right section: Filter buttons -->
        <b-col
          cols="12"
          lg="4"
          class="d-flex justify-content-lg-end mt-1 mt-lg-0"
        >
          <Button
            :label="$t('Filter')"
            size="md"
            icon="FilterIcon"
            show-icon
            class="mr-1"
            @click="showModal('modal-filter')"
          />
          <Button
            :label="$t('Reset Filter')"
            size="md"
            icon="RotateCwIcon"
            show-icon
            @click="resetFilter"
          />
        </b-col>
      </b-row>
    </div>

    <b-table
      ref="accountBalanceTableRef"
      :items="reportData"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      responsive
      :fields="translatedTableHeaders"
      primary-key="accountNumber"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    >

      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>
      <template #cell(openingBalance)="data">
        <span class="text-nowrap">
          {{ toBalance(data.value) }}
        </span>
      </template>

      <template #cell(grossInflows)="data">
        <span class="text-nowrap">
          {{ toBalance(data.value) }}
        </span>
      </template>

      <template #cell(grossOutflows)="data">
        <span class="text-nowrap">
          {{ toBalance(data.value) }}
        </span>
      </template>

      <template #cell(closingBalance)="data">
        <span class="text-nowrap">
          {{ toBalance(data.value) }}
        </span>
      </template>
      <template #cell(lastActivityDate)="data">
        <span class="text-nowrap">
          {{ data.value ? formatCustomDate(data.value) : '' }}
        </span>
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      no-close-on-backdrop
      :title="$t('Filters')"
      modal-footer
      @ok="handleSubmit"
      @cancel="resetFilters"
    >
      <validation-observer ref="accountBalanceMovementRules">
        <b-form>
          <b-row class="match-height">
            <!-- Account Name -->
            <b-col md="6">
              <InputField
                id="account-name"
                v-model="filter.accountName"
                :label="$t('Account Name')"
                :placeholder="$t('Enter Account Name')"
                type="text"
                for="account-name"
              />
            </b-col>
            <!-- Account Name -->
            <!-- Account Number -->
            <b-col md="6">
              <InputField
                id="account-number"
                v-model="filter.accountNumber"
                :label="$t('Account Number')"
                :placeholder="$t('Enter Account Number')"
                type="text"
                for="account-number"
              />
            </b-col>
          <!-- Account Number -->
          </b-row>

          <b-row class="match-height">
            <!-- Start Date -->
            <b-col md="6">
              <DatePickerField
                id="financial-date-from"
                v-model="filter.financialDateFrom"
                :min-date="new Date()"
                :label="$t('Financial Date (From)')"
                rules="required"
                :error-name="$t('Financial Date From')"
                :config="datePickerConfig"
                :placeholder="$t('Date From')"
              />
            </b-col>
            <!-- Start Date -->
            <!-- End Date -->
            <b-col md="6">
              <DatePickerField
                id="financial-date-to"
                v-model="filter.financialDateTo"
                rules="required"
                :min-date="new Date()"
                :label="$t('Financial Date (To)')"
                :error-name="$t('Financial Date To')"
                :config="datePickerConfig"
                :placeholder="$t('Date To')"
              />
            </b-col>
          <!-- End Date -->
          </b-row>

          <b-row class="match-height">
            <!-- Product -->
            <b-col md="6">
              <SelectField
                id="product"
                v-model="filter.productCode"
                :reduce="option => option.value"
                :label="$t('Product Name')"
                :placeholder="$t('Select')"
                :options="productOptions"
                item="label"
              />
            </b-col>
            <!-- Product -->
            <!-- Branch -->
            <b-col md="6">
              <SelectField
                id="branch"
                v-model="filter.branchCode"
                :reduce="option => option.value"
                :label="$t('Branch')"
                :placeholder="$t('Select')"
                :options="branchOptions"
                item="label"
                @input="getBranchValue(filter.branchCode)"
              />
            </b-col>
          <!-- Branch -->
          </b-row>
        </b-form>
      </validation-observer>
      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BSpinner, BModal, BForm, BDropdown, BDropdownItem,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import { tableMixins } from '@core/mixins/table'
import { modalMixins } from '@core/mixins/modal'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { permissionMixin } from '@core/mixins/permission'
import { mapActions, mapGetters } from 'vuex'
import Ripple from 'vue-ripple-directive'
import { heightFade } from '@core/directives/animations'
import { toBalance, formatAmount, toFormatAmount } from '@/helpers/formatAmount'
import toNumber from '@/helpers/toNumber'
import { toCustomDate, toDateWithYearFirst, getMinusOneDayDate } from '@/helpers/formatDate'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import SelectField from '../../../forms/SelectField.vue'
import DatePickerField from '../../../forms/DatePickerField.vue'
import InputField from '../../../forms/InputField.vue'
import Button from '../../../components/button/Button.vue'
import { reportDataDetails } from '../../../../helpers/data'
import { reportPermissions } from '../../../../helpers/permissions'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    Button,
    BSpinner,
    BTable,
    TablePagination,
    InputField,
    SelectField,
    BModal,
    BForm,
    BDropdown,
    BDropdownItem,
    TableEntries,
    DatePickerField,
    ValidationObserver,
  },
  directives: {
    Ripple,
    'height-fade': heightFade,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      formatAmount,
      toBalance,
      toFormatAmount,
      toDateWithYearFirst,
      query: '',
      currentDate: '',
      statusToggleLabel: '',
      isReadonly: false,
      buttonLabel: this.$t('Save'),
      isEditable: false,
      isBusy: false,
      isDisabled: true,
      required,
      modalTitle: this.$t('Account Balance Movement Report'),
      sortBy: 'name',
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        { key: 'accountNumber', sortable: true, label: 'Account Number' },
        { key: 'productName', sortable: true, label: 'Product Name' },
        { key: 'branchName', sortable: true, label: 'Branch Name' },
        { key: 'accountOfficer', sortable: true, label: 'Account Officer' },
        { key: 'openingBalance', sortable: true, label: 'Beginning Balance' },
        { key: 'grossInflows', sortable: true, label: 'Gross Inflows' },
        { key: 'grossOutflows', sortable: true, label: 'Gross Outflows' },
        { key: 'closingBalance', sortable: true, label: 'Ending Balance' },
        { key: 'lastActivityDate', sortable: true, label: 'Last Activity Date' },
      ],
      filter: {
        accountNumber: '',
        accountName: '',
        financialDateFrom: null,
        financialDateTo: null,
        productCode: '',
        branchCode: '',
      },
      datePickerConfig: {
        dateFormat: 'Y-m-d',
        maxDate: 'today',
        defaultDate: '',
      },
      productOptions: [],
      branchOptions: [],
      reportData: [],
      allSearchedData: [],
      branchRefCode: '',
      selected: '',
      reportInfo: reportDataDetails,
    }
  },
  computed: {
    ...mapGetters({
      userDetails: 'auth/getCurrentUser',
      currentFinancialDate: 'operations/getCurrentFinancialDate',
    }),
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    getPreviousFinancialDates() {
      return getMinusOneDayDate(this.currentFinancialDate.financialDate)
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchBalanceMovementReport()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchBalanceMovementReport()
    },
  },
  mounted() {
    this.fetchBalanceMovementReport()
    this.fetchBranches()
    this.fetchProduct()
    this.datePickerConfig.maxDate = this.getPreviousFinancialDates
  },
  methods: {
    ...mapActions({
      getProduct: 'account/getProducts',
      getBranch: 'operations/getBranchList',
      getBranchByCode: 'operations/getBranchByCode',
      getAccountBalanceMovement: 'reports/getAccountBalanceMovement',
      downloadAccountBalanceMovementReports: 'reports/downloadAccountBalanceMovementReports',
    }),
    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.filter.financialDateFrom = null
      this.filter.financialDateTo = null
      this.fetchBalanceMovementReport()
    },
    async fetchBranches() {
      try {
        const response = await this.getBranch({
          search: '',
          page: 0,
          limit: 0,
          permission: reportPermissions.ACCOUNT_BALANCE_MOVEMENT_REPORT,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.branchOptions = data.data
            .filter(dataItem => dataItem.status === 'Active')
            .map(item => ({
              label: item.name,
              value: item.code.toString(),
            }))
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async fetchProduct() {
      try {
        const response = await this.getProduct({
          search: '',
          page: 0,
          limit: 0,
          permission: reportPermissions.ACCOUNT_BALANCE_MOVEMENT_REPORT,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.productOptions = data.data.filter(dataItem => dataItem.status === 'Active' && dataItem.type !== 'Loan').map(item => ({ label: item.name, value: item.code }))
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async getBranchValue(dataValue) {
      if (dataValue !== null) {
        try {
          const response = await this.getBranchByCode(dataValue)
          const { status, data } = response
          if (status === 200 && data) {
            this.branchRefCode = data
          }
        } catch (error) {
          this.alertError({ text: error })
        } finally {
          this.isBusy = false
        }
      }
    },
    async handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.accountBalanceMovementRules.validate().then(async success => {
        if (success) {
          this.fetchBalanceMovementReport()
        }
      })
    },
    async fetchBalanceMovementReport() {
      const dt = new Date(this.currentFinancialDate.financialDate)
      const year = dt.getFullYear()
      const month = dt.getMonth()
      const firstDayMonth = new Date(year, month, 1)
      try {
        this.hideModal('modal-filter')
        this.isBusy = true
        if (this.filter.financialDateFrom === null && this.filter.financialDateTo === null) {
          this.currentDate = {
            financialDateFrom: toDateWithYearFirst(firstDayMonth),
            financialDateTo: toDateWithYearFirst(this.currentFinancialDate.financialDate),
          }
        }
        const response = await this.getAccountBalanceMovement({
          search: this.filter.financialDateFrom !== null ? this.filter : this.currentDate,
          page: this.currentPage,
          limit: this.perPage,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.reportData = data.data.map(item => ({
            ...item,
            closingBalance: Number(item.closingBalance),
            grossInflows: Number(item.grossInflows),
            grossOutflows: Number(item.grossOutflows),
            openingBalance: Number(item.openingBalance),
          }))
          this.totalCount = data.recordCount
          this.isBusy = false
        } else {
          this.reportData = []
          this.totalCount = 0
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    handleDateChange() {
      if (this.filter.financialDateFrom && this.filter.financialDateTo) {
        this.isDisabled = false
      } else {
        this.isDisabled = true
      }
    },
    async csvExport() {
      try {
        this.isBusy = true
        const response = await this.downloadAccountBalanceMovementReports({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          fileExportFormat: 1,
          permission: reportPermissions.EXPORT_ACCOUNT_BALANCE_MOVEMENT_REPORT,
        })
        if (response.status === 200) {
          this.alertSuccessCustom({
            text: 'Your download is in progress and might take a little longer. Go to your ',
            link: {
              text: 'Download Manager',
              route: { name: 'download-manager' },
            },
            afterLinkText: ' to access the completed file.',
          })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async excelExport() {
      try {
        this.isBusy = true
        const response = await this.downloadAccountBalanceMovementReports({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          fileExportFormat: 0,
          permission: reportPermissions.EXPORT_ACCOUNT_BALANCE_MOVEMENT_REPORT,
        })
        if (response.status === 200) {
          this.alertSuccessCustom({
            text: 'Your download is in progress and might take a little longer. Go to your',
            link: {
              text: 'Download Manager',
              route: { name: 'download-manager' },
            },
            afterLinkText: ' to access the completed file.',
          })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async pdfExport() {
      try {
        this.isBusy = true
        const response = await this.downloadAccountBalanceMovementReports({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          fileExportFormat: 2,
          permission: reportPermissions.EXPORT_ACCOUNT_BALANCE_MOVEMENT_REPORT,
        })
        if (response.status === 200) {
          this.alertSuccessCustom({
            text: 'Your download is in progress and might take a little longer. Go to your',
            link: {
              text: 'Download Manager',
              route: { name: 'download-manager' },
            },
            afterLinkText: ' to access the completed file.',
          })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    formatNumber: toNumber,
    formatCustomDate: toCustomDate,
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

.invoice-filter-select {
  min-width: 190px;

  ::v-deep .vs__selected-options {
    flex-wrap: nowrap;
  }

  ::v-deep .vs__selected {
    width: 100px;
  }
}
</style>

<style lang="scss">
    @import '@core/scss/vue/libs/vue-select.scss';
</style>
